// @ts-nocheck
// Public, no-login, single-profile FIRST-LOGIN embed. Opening /c/:code streams
// this ONE profile's real browser (the bot's Chrome) so the customer can
// complete the Facebook login — username/password + any 2FA/checkpoint — live,
// in a mobile view. No account/dashboard access; scoped entirely by the code.
//
// Flow: a pre-step form collects the FB username + password. We store them on
// the profile (with history) and, once the mobile login page is streaming, the
// bot autofills them into the form. The customer reviews, taps "Log In", and
// handles any 2FA live in the stream. A refresh button reloads the streamed page.
//
// Typing uses the same "native local input over the screencast" trick as the
// fleet admin preview: when the remote page focuses a text field, the bot sends
// its rect; we render a real <input> over it (so the mobile soft-keyboard opens)
// and mirror every keystroke back with input_set_text. Pointer + coordinates are
// letterbox-aware (object-fit: contain), so taps land exactly on the target.
import React, {useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

type Phase = 'loading' | 'form' | 'connecting' | 'streaming' | 'started' | 'success' | 'expired' | 'error'

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

const ProfileLoginEmbed = () => {
  const {code} = useParams()
  const [phase, setPhase] = useState<Phase>('loading')
  const [profile, setProfile] = useState<any>(null)
  const [isMobileView, setIsMobileView] = useState(true)
  // Streaming the bot's live browser is behind an admin flag (admin settings →
  // "מסך התחברות חי"). Off by default: the customer just hands over the
  // credentials and gets told the login has started. Assume off until the API
  // says otherwise, so the session never flashes up on a slow response.
  const [liveSession, setLiveSession] = useState(false)
  const [statusText, setStatusText] = useState('טוען…')

  // Pre-step form state.
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const credsRef = useRef<{username: string; password: string}>({username: '', password: ''})
  const autofilledRef = useRef(false)

  const imgRef = useRef<HTMLImageElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const lastMetaRef = useRef<any>(null)
  const statusPollRef = useRef<any>(null)

  // Native overlay input (typing bridge). overlayInfo drives visibility/style;
  // overlayInfoRef mirrors it for the imperative per-frame repositioning.
  const [overlayInfo, setOverlayInfo] = useState<any>(null)
  const overlayInfoRef = useRef<any>(null)
  const overlayRef = useRef<HTMLInputElement>(null)

  // Rendered outside <App>, so hide the boot splash ourselves.
  useEffect(() => {
    const s = document.getElementById('splash-screen')
    if (s) s.style.display = 'none'
    document.body.classList.remove('page-loading')
  }, [])

  const send = (o: any) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(o)) } catch (e) {}
    }
  }

  const sendAutofill = () => {
    const {username: u, password: p} = credsRef.current
    if (!u && !p) return
    send({kind: 'autofill_login', username: u, password: p})
  }

  // ---- coordinate mapping (object-fit: contain aware) ----------------------
  // The <img> is object-fit:contain inside a fixed-aspect box, so the actual
  // rendered picture is letterboxed. Everything below maps through that geometry
  // — using the img's natural size for the picture aspect and the frame's
  // deviceWidth/Height for the remote page's CSS-pixel coordinate space.
  const imgGeom = () => {
    const img = imgRef.current
    if (!img) return null
    const box = img.getBoundingClientRect()
    const meta = lastMetaRef.current
    const dw = (meta && meta.deviceWidth) || img.naturalWidth || box.width
    const dh = (meta && meta.deviceHeight) || img.naturalHeight || box.height
    const natW = img.naturalWidth || dw
    const natH = img.naturalHeight || dh
    const scale = Math.min(box.width / natW, box.height / natH) || 1
    const renderedW = natW * scale
    const renderedH = natH * scale
    const offX = (box.width - renderedW) / 2
    const offY = (box.height - renderedH) / 2
    return {box, dw, dh, renderedW, renderedH, offX, offY}
  }

  // client (viewport) coords → remote page CSS px
  const toPage = (clientX: number, clientY: number) => {
    const g = imgGeom()
    if (!g) return {x: 0, y: 0}
    const xInImg = clamp((clientX - g.box.left - g.offX) / g.renderedW, 0, 1)
    const yInImg = clamp((clientY - g.box.top - g.offY) / g.renderedH, 0, 1)
    return {x: xInImg * g.dw, y: yInImg * g.dh}
  }

  // remote page rect (CSS px) → position within the img element box
  const pageRectToBox = (r: any) => {
    const g = imgGeom()
    if (!g || !r) return null
    return {
      left: g.offX + (r.x / g.dw) * g.renderedW,
      top: g.offY + (r.y / g.dh) * g.renderedH,
      w: (r.w / g.dw) * g.renderedW,
      h: (r.h / g.dh) * g.renderedH,
    }
  }

  const positionOverlay = () => {
    const info = overlayInfoRef.current
    const el = overlayRef.current
    if (!info || !el) return
    const p = pageRectToBox(info.rect)
    if (!p) return
    el.style.left = p.left + 'px'
    el.style.top = p.top + 'px'
    el.style.width = Math.max(60, p.w) + 'px'
    el.style.height = Math.max(26, p.h) + 'px'
  }

  // ---- session bootstrap: fetch profile, show the form ---------------------
  useEffect(() => {
    let cancelled = false
    const boot = async () => {
      let info
      try {
        const r = await axios.get(`${API_URL}/connect-link/${code}`)
        info = r.data
      } catch (e: any) {
        if (!cancelled) setPhase(e?.response?.status === 410 ? 'expired' : 'error')
        return
      }
      if (cancelled) return
      setProfile(info.profile)
      setLiveSession(info.live_session_enabled === true)
      // Prefill whatever we already have on the profile. Most opens of this page
      // are a RE-login (session died), where the credentials haven't changed —
      // making the customer retype them is the main way this flow stalls. Typing
      // over a prefilled field is a real change and gets archived server-side.
      if (info.profile?.facebook_username) setUsername(info.profile.facebook_username)
      if (info.profile?.facebook_password) setPassword(info.profile.facebook_password)
      if (info.profile?.is_logged_in) { setPhase('success'); return }
      setPhase('form')
    }
    boot()
    return () => {
      cancelled = true
      if (statusPollRef.current) clearInterval(statusPollRef.current)
      try { wsRef.current?.close() } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  // ---- form submit: store creds, then open the stream ----------------------
  const onSubmitCreds = async (e?: any) => {
    if (e) e.preventDefault()
    if (submitting) return
    if (!username.trim() || !password) return
    setSubmitting(true)
    credsRef.current = {username: username.trim(), password}
    autofilledRef.current = false
    try { await axios.post(`${API_URL}/connect-link/${code}/credentials`, {username: username.trim(), password}) } catch (e) {}

    // Credentials-only mode (the default): start the profile and leave the
    // customer on the "pending — a rep is on it" screen. We still call /session
    // — that is what starts the browser — we just don't open the stream it hands
    // back. The poll keeps running only to stamp the link used server-side once
    // the profile lands; it deliberately does NOT flip the screen to "connected",
    // because is_logged_in is not the same as a rep having verified the account.
    if (!liveSession) {
      setPhase('started')
      setSubmitting(false)
      try { await axios.post(`${API_URL}/connect-link/${code}/session`) } catch (e) {}
      statusPollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`${API_URL}/connect-link/${code}/status`)
          if (r.data?.is_logged_in) clearInterval(statusPollRef.current)
        } catch (e) {}
      }, 6000)
      return
    }

    setPhase('connecting')
    setStatusText('מתחברים לדפדפן…')
    let ws_url
    try {
      const r = await axios.post(`${API_URL}/connect-link/${code}/session`)
      ws_url = r.data.ws_url
    } catch (err: any) {
      setPhase(err?.response?.status === 410 ? 'expired' : 'error')
      setSubmitting(false)
      return
    }
    if (!ws_url) { setPhase('error'); setSubmitting(false); return }
    openStream(ws_url)
    // Same as above: landing logged-in closes the stream and stamps the link
    // used, but the customer is left on "pending, a rep is verifying" rather
    // than a success screen we can't back up yet.
    statusPollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/connect-link/${code}/status`)
        if (r.data?.is_logged_in) {
          clearInterval(statusPollRef.current)
          setPhase('started')
          try { wsRef.current?.close() } catch (e) {}
        }
      } catch (e) {}
    }, 4000)
    setSubmitting(false)
  }

  const applyOverlay = (info: any) => {
    overlayInfoRef.current = info
    setOverlayInfo(info)
    // Position + prime the value on the next tick (after the element mounts).
    setTimeout(() => {
      const el = overlayRef.current
      if (!el) return
      el.type = (info && String(info.type).toLowerCase() === 'password') ? 'password' : 'text'
      el.value = (info && info.value) || ''
      positionOverlay()
      try { el.focus({preventScroll: true}) } catch (e) { try { el.focus() } catch (e2) {} }
      try { el.setSelectionRange(el.value.length, el.value.length) } catch (e) {}
    }, 0)
  }

  const clearOverlay = () => {
    overlayInfoRef.current = null
    setOverlayInfo(null)
  }

  const openStream = (wsUrl: string) => {
    let ws: WebSocket
    try { ws = new WebSocket(wsUrl) } catch (e) { setPhase('error'); return }
    wsRef.current = ws
    ws.onopen = () => setStatusText('ממתין לתמונה…')
    ws.onclose = () => { setStatusText('החיבור נותק') }
    ws.onerror = () => { setStatusText('שגיאת חיבור') }
    ws.onmessage = (ev) => {
      let m: any
      try { m = JSON.parse(ev.data) } catch (e) { return }
      if (m.kind === 'screencast_frame') {
        if (imgRef.current) imgRef.current.src = 'data:image/jpeg;base64,' + m.data
        lastMetaRef.current = m.metadata || null
        if (overlayInfoRef.current) positionOverlay()
        setPhase((p) => (p === 'streaming' ? p : 'streaming'))
      } else if (m.kind === 'screencast_started') {
        setStatusText('מחובר')
        if (typeof m.mobile === 'boolean') setIsMobileView(m.mobile)
      } else if (m.kind === 'input_focused') {
        applyOverlay(m)
      } else if (m.kind === 'input_blurred') {
        clearOverlay()
      } else if (m.kind === 'page_url') {
        const url = String(m.url || '')
        if (!autofilledRef.current && /facebook\.com\/login|\/login/i.test(url)) {
          autofilledRef.current = true
          setTimeout(sendAutofill, 800)
        }
      } else if (m.kind === 'preview_ready') {
        setStatusText('מכינים דפדפן…')
      } else if (m.kind === 'screencast_error') {
        setStatusText('שגיאה: ' + m.error)
      }
    }
  }

  // ---- pointer (mouse + touch) input --------------------------------------
  const pressAt = (cx: number, cy: number) => {
    const p = toPage(cx, cy)
    send({kind: 'cdp_input', event: 'mouse', type: 'mousePressed', x: p.x, y: p.y, button: 'left', clickCount: 1})
  }
  const moveAt = (cx: number, cy: number) => {
    const p = toPage(cx, cy)
    send({kind: 'cdp_input', event: 'mouse', type: 'mouseMoved', x: p.x, y: p.y, button: 'left'})
  }
  const releaseAt = (cx: number, cy: number) => {
    const p = toPage(cx, cy)
    send({kind: 'cdp_input', event: 'mouse', type: 'mouseReleased', x: p.x, y: p.y, button: 'left', clickCount: 1})
  }

  // Focus the overlay input synchronously inside the tap gesture — required so
  // the mobile soft-keyboard opens. It sits off-screen until the remote page
  // reports a focused field (input_focused), at which point applyOverlay moves
  // it over the field. If the tap didn't hit a field, focusout hides it again.
  const primeKeyboard = () => { try { overlayRef.current?.focus({preventScroll: true}) } catch (e) {} }

  const onImgMouseDown = (e: React.MouseEvent) => { e.preventDefault(); pressAt(e.clientX, e.clientY) }
  const onImgMouseUp = (e: React.MouseEvent) => { e.preventDefault(); releaseAt(e.clientX, e.clientY); primeKeyboard() }
  const onImgMouseMove = (e: React.MouseEvent) => { if (e.buttons & 1) moveAt(e.clientX, e.clientY) }
  const onImgWheel = (e: React.WheelEvent) => {
    const p = toPage(e.clientX, e.clientY)
    send({kind: 'cdp_input', event: 'mouse', type: 'mouseWheel', x: p.x, y: p.y, deltaX: e.deltaX, deltaY: e.deltaY})
  }

  const touchActive = useRef(false)
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]; if (!t) return
    touchActive.current = true
    pressAt(t.clientX, t.clientY)
  }
  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchActive.current) return
    const t = e.touches[0]; if (!t) return
    moveAt(t.clientX, t.clientY)
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0]
    if (t) releaseAt(t.clientX, t.clientY)
    touchActive.current = false
    primeKeyboard()
  }

  // ---- overlay input events (mirror to remote) -----------------------------
  const onOverlayInput = (e: any) => {
    send({kind: 'input_set_text', value: e.target.value})
  }
  const onOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send({kind: 'cdp_input', event: 'key', type: 'keyDown', key: 'Enter', code: 'Enter'})
      send({kind: 'cdp_input', event: 'key', type: 'keyUp', key: 'Enter', code: 'Enter'})
    } else if (e.key === 'Escape') {
      e.preventDefault()
      send({kind: 'input_clear_focus'})
      clearOverlay()
    }
  }

  const toggleView = () => {
    const next = !isMobileView
    setIsMobileView(next)
    send({kind: 'mobile_emulate', enabled: next})
  }

  const refreshPage = () => { send({kind: 'page_reload'}) }

  // ---- render --------------------------------------------------------------
  const shell = (children: any) => (
    <div dir='rtl' style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0d1117', fontFamily: 'system-ui, sans-serif', padding: 12, color: '#fff'}}>
      {children}
    </div>
  )

  if (phase === 'expired') {
    return shell(
      <div style={{textAlign: 'center', maxWidth: 380}}>
        <div style={{fontSize: 42, marginBottom: 10}}>⌛</div>
        <h3 style={{color: '#fff'}}>הקישור פג תוקף</h3>
        <p style={{color: '#8b949e'}}>בקשו מבעל החשבון קישור התחברות חדש.</p>
      </div>
    )
  }
  // Credentials handed over. This screen never claims success — a login is only
  // really done once a rep has cleared whatever Facebook throws up (2FA, "was
  // this you", a checkpoint), and telling the customer "connected ✓" before that
  // is a promise we can't keep. So: a spinner, "ההתחברות בהמתנה", and who has it
  // now. It stays on this state; the rep is the one who confirms.
  if (phase === 'started') {
    return shell(
      <div style={{textAlign: 'center', maxWidth: 400}}>
        <div style={{width: 46, height: 46, margin: '0 auto 18px', border: '4px solid #30363d', borderTopColor: '#1877f2', borderRadius: '50%', animation: 'embspin 0.8s linear infinite'}} />
        <style>{`@keyframes embspin{to{transform:rotate(360deg)}}`}</style>
        <h3 style={{color: '#fff'}}>ההתחברות בהמתנה…</h3>
        <p style={{color: '#8b949e', lineHeight: 1.7, marginTop: 10}}>
          קיבלנו את פרטי ההתחברות של {profile?.name || 'הפרופיל'} והעברנו אותם לנציג שיוודא שאתם מחוברים.
          <br />
          אפשר לסגור את החלון — אין צורך להמתין כאן.
        </p>
        <p style={{color: '#6e7681', fontSize: 12.5, marginTop: 14}}>
          נעדכן אתכם ברגע שהפרופיל יחובר.
        </p>
      </div>
    )
  }
  if (phase === 'success') {
    return shell(
      <div style={{textAlign: 'center', maxWidth: 380}}>
        <div style={{fontSize: 52, marginBottom: 10}}>✅</div>
        <h3 style={{color: '#fff'}}>הפרופיל חובר בהצלחה</h3>
        <p style={{color: '#8b949e'}}>{profile?.name} מחובר עכשיו לפייסבוק. אפשר לסגור את החלון.</p>
      </div>
    )
  }
  if (phase === 'error') {
    return shell(
      <div style={{textAlign: 'center', maxWidth: 380}}>
        <div style={{fontSize: 42, marginBottom: 10}}>⚠️</div>
        <h3 style={{color: '#fff'}}>לא הצלחנו לפתוח את הדפדפן</h3>
        <p style={{color: '#8b949e'}}>נסו לרענן את הדף בעוד רגע.</p>
      </div>
    )
  }
  if (phase === 'loading') {
    return shell(
      <div style={{textAlign: 'center'}}>
        <div style={{width: 42, height: 42, border: '4px solid #30363d', borderTopColor: '#1877f2', borderRadius: '50%', animation: 'embspin 0.8s linear infinite'}} />
        <style>{`@keyframes embspin{to{transform:rotate(360deg)}}`}</style>
      </div>
    )
  }

  // ---- pre-step credential form (Facebook-styled) -------------------------
  if (phase === 'form') {
    const ready = username.trim() && password
    return (
      <div dir='rtl' style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', fontFamily: 'Helvetica, Arial, system-ui, sans-serif', padding: 16}}>
        <div style={{width: '100%', maxWidth: 396, textAlign: 'center'}}>
          {/* Facebook wordmark */}
          <div style={{color: '#1877f2', fontSize: 56, fontWeight: 700, letterSpacing: '-2px', lineHeight: 1, marginBottom: 22, fontFamily: 'Helvetica, Arial, sans-serif'}}>
            facebook
          </div>

          <div style={{background: '#fff', borderRadius: 12, padding: '20px 20px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.12)'}}>
            {profile?.name && (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16}}>
                {profile?.avatar && <img src={profile.avatar} alt='' style={{width: 32, height: 32, borderRadius: '50%'}} />}
                <span style={{fontSize: 15, fontWeight: 600, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260}}>{profile.name}</span>
              </div>
            )}

            {(profile?.facebook_username || profile?.facebook_password) && (
              <div style={{background: '#f0f7ff', border: '1px solid #cfe3ff', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13, color: '#385898', lineHeight: 1.5}}>
                מילאנו עבורכם את הפרטים ששמורים אצלנו. אם הסיסמה השתנתה — עדכנו אותה כאן.
              </div>
            )}

            <form onSubmit={onSubmitCreds}>
              {/* Browser autofill is suppressed on both fields. This form looks
                  like a facebook.com login, so Chrome/Safari happily drop in a
                  saved Facebook password — for whoever is holding the phone, not
                  for the profile being connected. The only credentials that
                  belong here are the ones WE hold, filled from the API above.
                  Non-standard name= plus autoComplete='new-password' is what
                  actually stops it; autoComplete='off' alone is ignored. */}
              <input
                type='text' value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder='אימייל או מספר טלפון'
                name='fbz-identifier' autoComplete='off' data-lpignore='true' data-1p-ignore='true'
                autoCapitalize='off' autoCorrect='off' spellCheck={false} dir='rtl'
                style={{width: '100%', boxSizing: 'border-box', marginBottom: 12, padding: '14px 16px', background: '#fff', color: '#1c1e21', border: '1px solid #dddfe2', borderRadius: 6, fontSize: 16, outline: 'none'}}
              />
              <div style={{position: 'relative', marginBottom: 12}}>
                <input
                  type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder='סיסמה'
                  name='fbz-secret' autoComplete='new-password' data-lpignore='true' data-1p-ignore='true'
                  autoCapitalize='off' autoCorrect='off' spellCheck={false} dir='rtl'
                  style={{width: '100%', boxSizing: 'border-box', padding: '14px 16px', background: '#fff', color: '#1c1e21', border: '1px solid #dddfe2', borderRadius: 6, fontSize: 16, outline: 'none'}}
                />
                <button type='button' onClick={() => setShowPass(!showPass)}
                  style={{position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#606770', cursor: 'pointer', fontSize: 18}}>
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>

              <button type='submit' disabled={!ready || submitting}
                style={{width: '100%', padding: '13px', background: '#1877f2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 19, fontWeight: 700, cursor: ready && !submitting ? 'pointer' : 'default', opacity: ready && !submitting ? 1 : 0.65}}>
                {submitting ? 'מתחבר…' : 'התחברות'}
              </button>
            </form>

            <div style={{color: '#1877f2', fontSize: 14, marginTop: 16}}>שכחת את הסיסמה?</div>
          </div>

          <p style={{color: '#8a8d91', fontSize: 12, marginTop: 18, lineHeight: 1.5}}>
            הפרטים ישמשו להתחברות לפרופיל זה בלבד. בשלב הבא אפשר לאשר את ההתחברות ולהשלים אימות דו-שלבי אם נדרש.
          </p>
        </div>
      </div>
    )
  }

  // ---- streaming (+ connecting) -------------------------------------------
  // Height-first sizing, shared by the frame and the header/caption around it so
  // they stay the same width as the picture.
  const frameH = 'min(calc(100vh - 150px), 920px)'
  const frameW = isMobileView ? `calc(${frameH} * 390 / 844)` : `calc(${frameH} * 16 / 10)`
  return shell(
    <>
      <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, width: '100%', maxWidth: frameW, justifyContent: 'space-between'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
          {profile?.avatar && <img src={profile.avatar} alt='' style={{width: 28, height: 28, borderRadius: '50%'}} />}
          <span style={{fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {profile?.name || 'התחברות לפייסבוק'}
          </span>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
          <button onClick={refreshPage} title='רענן דף'
            style={{background: '#21262d', color: '#fff', border: '1px solid #30363d', borderRadius: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer'}}>
            🔄
          </button>
          <button onClick={sendAutofill} title='מלא פרטים'
            style={{background: '#21262d', color: '#fff', border: '1px solid #30363d', borderRadius: 8, padding: '6px 10px', fontSize: 13, cursor: 'pointer'}}>
            🔑
          </button>
          <button onClick={toggleView}
            style={{background: '#21262d', color: '#fff', border: '1px solid #30363d', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'}}>
            {isMobileView ? '🖥️ מחשב' : '📱 נייד'}
          </button>
        </div>
      </div>

      {/* Size the stream off the VIEWPORT HEIGHT, not a fixed 430px width. The
          streamed page is phone-shaped (390x844): pinned to 430px wide it came out
          ~930px tall — taller than a laptop screen, so the picture was letterboxed
          down to a small phone floating in dead space. Driving from the height and
          letting the width follow the ratio makes it as large as the screen allows.
          minWidth keeps a short/landscape window from collapsing it to a sliver. */}
      <div style={{position: 'relative', height: frameH, aspectRatio: isMobileView ? '390 / 844' : '16 / 10', width: 'auto', minWidth: 300, maxWidth: '100%', background: '#000', borderRadius: 14, overflow: 'hidden', border: '1px solid #30363d'}}>
        <img
          ref={imgRef}
          alt=''
          draggable={false}
          onMouseDown={onImgMouseDown}
          onMouseUp={onImgMouseUp}
          onMouseMove={onImgMouseMove}
          onWheel={onImgWheel}
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{width: '100%', height: '100%', objectFit: 'contain', touchAction: 'none', userSelect: 'none', display: phase === 'streaming' ? 'block' : 'none'}}
        />
        {phase !== 'streaming' && (
          <div style={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14}}>
            <div style={{width: 42, height: 42, border: '4px solid #30363d', borderTopColor: '#1877f2', borderRadius: '50%', animation: 'embspin 0.8s linear infinite'}} />
            <div style={{color: '#8b949e', fontSize: 14}}>{statusText}</div>
            <style>{`@keyframes embspin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}
        {/* Native overlay input over the focused remote field ("type natively,
            sync remotely"). Off-screen + transparent until a field is focused so
            it can still be focused inside the tap gesture to raise the keyboard. */}
        <input
          ref={overlayRef}
          onInput={onOverlayInput}
          onKeyDown={onOverlayKeyDown}
          autoCapitalize='off'
          autoCorrect='off'
          spellCheck={false}
          dir='ltr'
          style={overlayInfo ? {
            position: 'absolute', boxSizing: 'border-box', padding: '2px 8px',
            border: '2px solid #f0c040', background: '#fffceb', color: '#1a1d22',
            font: '15px system-ui, sans-serif', borderRadius: 4, outline: 'none',
            boxShadow: '0 0 0 3px rgba(240,192,64,0.3)', zIndex: 10,
          } : {position: 'absolute', bottom: 0, right: 0, width: 1, height: 1, opacity: 0, padding: 0, border: 'none', pointerEvents: 'none'}}
        />
      </div>

      <p style={{color: '#8b949e', fontSize: 12.5, marginTop: 10, textAlign: 'center', maxWidth: frameW}}>
        מילאנו את פרטי ההתחברות. הקישו על שדה כדי לערוך · לחצו "Log In" והשלימו אימות אם נדרש. 🔑 מילוי חוזר · 🔄 רענון.
      </p>
    </>
  )
}

export default ProfileLoginEmbed
